/**
 * CoachPlaybook - Studio Control Panel
 * Version: 0.6.0
 * Description: Right-side control panel for Playbook Studio — play config, formations,
 *              player/defender management, and play library in collapsible accordion sections.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../../utils/theme';

/* ══════════════════════════════════════════════════════════
   ControlPanel
   ══════════════════════════════════════════════════════════ */
const ControlPanel = ({
  currentPlay,
  plays = [],
  sportConfig,
  selectedPosition,
  showDefenders = false,
  onPlayUpdate,
  onPlaySelect,
  onPlayCreate,
  onPlayDelete,
  onPositionSelect,
  onFormationApply,
  onToggleDefenders,
}) => {
  /* ─── theme ─── */
  const { mode, theme } = useTheme();

  /* ─── palette (derived from theme) ─── */
  const C = {
    panelBg:     theme.bgPanel,
    surface:     theme.bgCard,
    border:      theme.border,
    borderLight: theme.borderLight,
    text:        theme.text,
    textMuted:   theme.textSecondary,
    textDim:     theme.textMuted,
    accent:      theme.accent,
    accentHover: theme.accentHover,
    danger:      theme.danger,
    dangerDim:   mode === 'dark' ? '#7f1d1d' : '#fecaca',
    success:     theme.success,
    warning:     theme.warning,
    highlight:   mode === 'dark' ? '#1d4ed8' : '#dbeafe',
  };

  /* ─── shared inline helpers ─── */
  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 13,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const pillStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '3px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${active ? C.accent : C.border}`,
    backgroundColor: active ? C.accent + '22' : C.surface,
    color: active ? C.accent : C.textMuted,
    transition: 'all 0.15s',
  });

  /* ─── Section header (accordion) ─── */
  const SectionHeader = ({ label, isOpen, onToggle, count }) => (
    <button
      onClick={onToggle}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 16px',
        backgroundColor: isOpen ? (mode === 'dark' ? '#1a2744' : '#e0e7ff') : 'transparent',
        border: 'none',
        borderBottom: `1px solid ${C.border}`,
        cursor: 'pointer',
        transition: 'background 0.15s',
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: C.textMuted }}>
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {count !== undefined && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: C.textDim,
            backgroundColor: C.surface, padding: '2px 8px', borderRadius: 999,
          }}>
            {count}
          </span>
        )}
        <span style={{
          fontSize: 10, color: C.textDim,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
          display: 'inline-block',
        }}>
          ▼
        </span>
      </span>
    </button>
  );

  /* ── local state ── */
  const [sections, setSections] = useState({
    formations: false,
    players: true,
    defenders: false,
    library: true,
  });
  const [newTag, setNewTag] = useState('');
  const [librarySearch, setLibrarySearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const scrollRef = useRef(null);
  const gifInputRef = useRef(null);

  const toggle = (key) => setSections((prev) => ({ ...prev, [key]: !prev[key] }));

  /* ── derived ── */
  const playTypeGroups = sportConfig?.playTypes || {};
  const allPlayTypes = Object.entries(playTypeGroups).map(([group, items]) => ({ group, items }));
  const formations = sportConfig?.formations || [];
  const positions = currentPlay?.positions || [];
  const defenders = currentPlay?.defenders || [];
  const tags = currentPlay?.tags || [];
  const playType = currentPlay?.type || '';
  const playCategory = currentPlay?.category || '';

  /* ── helpers ── */
  const updatePlay = (patch) => onPlayUpdate?.({ ...currentPlay, ...patch });

  const addTag = () => {
    const t = newTag.trim();
    if (!t || tags.includes(t)) return;
    updatePlay({ tags: [...tags, t] });
    setNewTag('');
  };

  const removeTag = (tag) => updatePlay({ tags: tags.filter((t) => t !== tag) });

  /* ─── GIF attachment ─── */
  const handleGifSelect = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      updatePlay({ gifUrl: reader.result });
    };
    reader.readAsDataURL(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  }, [currentPlay, onPlayUpdate]);

  const removeGif = useCallback(() => {
    updatePlay({ gifUrl: null });
  }, [currentPlay, onPlayUpdate]);

  const filteredPlays = plays.filter((p) => {
    if (!librarySearch) return true;
    const q = librarySearch.toLowerCase();
    return (
      (p.name || '').toLowerCase().includes(q) ||
      (p.type || '').toLowerCase().includes(q) ||
      (p.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  });

  /* ── type badge color map ── */
  const typeBadgeColor = (type) => {
    const map = { offense: '#3b82f6', defense: '#ef4444', outOfBounds: '#f59e0b', special: '#a855f7', transition: '#06b6d4' };
    return map[type] || C.textDim;
  };

  /* ═══════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════ */
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: C.panelBg,
        borderLeft: `1px solid ${C.border}`,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: C.text,
        overflow: 'hidden',
      }}
    >
      {/* ─── scrollable body ─── */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollbarWidth: 'thin',
          scrollbarColor: `${C.border} ${C.panelBg}`,
        }}
      >
        {/* ════════════════════════════════════════════════
           1. PLAY INFO  (always visible, not collapsible)
           ════════════════════════════════════════════════ */}
        <div style={{ padding: 16, borderBottom: `1px solid ${C.border}` }}>
          {/* Play name */}
          <input
            type="text"
            value={currentPlay?.name || ''}
            onChange={(e) => updatePlay({ name: e.target.value })}
            placeholder="Play name..."
            style={{ ...inputStyle, fontSize: 15, fontWeight: 600, marginBottom: 12 }}
          />

          {/* Sport badge */}
          {sportConfig && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 12px', borderRadius: 999,
                backgroundColor: (sportConfig.accentColor || C.accent) + '22',
                border: `1px solid ${(sportConfig.accentColor || C.accent) + '44'}`,
                fontSize: 12, fontWeight: 600, color: sportConfig.accentColor || C.accent,
              }}>
                {sportConfig.icon || '🏟️'} {sportConfig.name || 'Sport'}
              </span>
            </div>
          )}

          {/* Type selector */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
            Type
          </label>
          <select
            value={playType}
            onChange={(e) => updatePlay({ type: e.target.value, category: '' })}
            style={{ ...inputStyle, marginBottom: 12, cursor: 'pointer' }}
          >
            <option value="">Select type...</option>
            {allPlayTypes.map(({ group }) => (
              <option key={group} value={group}>
                {group.charAt(0).toUpperCase() + group.slice(1).replace(/([A-Z])/g, ' $1')}
              </option>
            ))}
          </select>

          {/* Category selector (driven by selected type) */}
          {playType && playTypeGroups[playType] && (
            <>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
                Category
              </label>
              <select
                value={playCategory}
                onChange={(e) => updatePlay({ category: e.target.value })}
                style={{ ...inputStyle, marginBottom: 12, cursor: 'pointer' }}
              >
                <option value="">Select category...</option>
                {playTypeGroups[playType].map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.label}</option>
                ))}
              </select>
            </>
          )}

          {/* Tags */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginBottom: 6 }}>
            Tags
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {tags.map((tag) => (
              <span key={tag} style={pillStyle(false)}>
                {tag}
                <span
                  onClick={() => removeTag(tag)}
                  style={{ cursor: 'pointer', color: C.danger, fontWeight: 700, marginLeft: 2 }}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTag()}
              placeholder="Add tag..."
              style={{ ...inputStyle, flex: 1, fontSize: 12 }}
            />
            <button
              onClick={addTag}
              style={{
                padding: '6px 14px', borderRadius: 6, border: 'none',
                backgroundColor: C.accent, color: '#fff', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', opacity: newTag.trim() ? 1 : 0.4,
              }}
            >
              +
            </button>
          </div>

          {/* GIF Attachment */}
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: C.textMuted, marginTop: 14, marginBottom: 6 }}>
            GIF Attachment
          </label>
          <input
            ref={gifInputRef}
            type="file"
            accept=".gif,image/gif"
            onChange={handleGifSelect}
            style={{ display: 'none' }}
          />
          {currentPlay?.gifUrl ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img
                src={currentPlay.gifUrl}
                alt="Play GIF"
                style={{
                  height: 60,
                  borderRadius: 6,
                  border: `1px solid ${C.border}`,
                  objectFit: 'contain',
                  backgroundColor: '#000',
                }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <button
                  onClick={() => onPlayUpdate?.({ ...currentPlay, _showGif: true })}
                  style={{
                    padding: '4px 10px', borderRadius: 5, border: `1px solid ${C.border}`,
                    backgroundColor: C.surface, color: C.text, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.text; }}
                >
                  View
                </button>
                <button
                  onClick={removeGif}
                  style={{
                    padding: '4px 10px', borderRadius: 5, border: `1px solid ${C.border}`,
                    backgroundColor: 'transparent', color: C.danger, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.dangerDim; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => gifInputRef.current?.click()}
              style={{
                padding: '8px 14px', borderRadius: 6,
                border: `1px dashed ${C.border}`,
                backgroundColor: 'transparent', color: C.textMuted,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s', width: '100%',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.color = C.accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.textMuted; }}
            >
              + Attach GIF
            </button>
          )}
        </div>

        {/* ════════════════════════════════════════════════
           2. FORMATIONS
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Formations" isOpen={sections.formations} onToggle={() => toggle('formations')} count={formations.length} />
        {sections.formations && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
              {formations.map((f) => {
                const isActive = currentPlay?.formation === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => onFormationApply?.(f)}
                    style={{
                      padding: '10px 8px',
                      backgroundColor: isActive ? C.accent + '22' : C.surface,
                      border: `1px solid ${isActive ? C.accent : C.border}`,
                      borderRadius: 8,
                      color: isActive ? C.accent : C.text,
                      fontSize: 12,
                      fontWeight: isActive ? 700 : 500,
                      cursor: 'pointer',
                      textAlign: 'center',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = C.border;
                        e.currentTarget.style.borderColor = C.borderLight;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.currentTarget.style.backgroundColor = C.surface;
                        e.currentTarget.style.borderColor = C.border;
                      }
                    }}
                  >
                    {f.name}
                    {f.type && (
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                        {f.type}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            {formations.length === 0 && (
              <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                No formations defined for this sport
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           3. PLAYERS
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Players" isOpen={sections.players} onToggle={() => toggle('players')} count={positions.length} />
        {sections.players && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {positions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: C.textDim, fontSize: 12 }}>
                No players on the surface
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {positions.map((pos) => {
                  const isSelected = selectedPosition === pos.id;
                  const hasBall = pos.hasBall || false;
                  return (
                    <button
                      key={pos.id}
                      onClick={() => onPositionSelect?.(pos.id)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '8px 12px',
                        backgroundColor: isSelected ? C.accent + '18' : 'transparent',
                        border: 'none',
                        borderLeft: `3px solid ${isSelected ? C.accent : 'transparent'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = C.surface; }}
                      onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* color dot */}
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%',
                        backgroundColor: pos.color || C.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        boxShadow: isSelected ? `0 0 0 2px ${C.accent}` : 'none',
                      }}>
                        {pos.positionLabel || '?'}
                      </span>

                      {/* info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {pos.playerName || pos.positionLabel || `Player ${pos.id}`}
                        </div>
                        <div style={{ fontSize: 10, color: C.textDim }}>
                          {pos.jerseyNumber ? `#${pos.jerseyNumber}` : pos.positionLabel || ''}
                        </div>
                      </div>

                      {/* has ball indicator */}
                      {hasBall && (
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          backgroundColor: C.warning, flexShrink: 0,
                          boxShadow: `0 0 6px ${C.warning}`,
                        }}
                          title="Has ball"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           4. DEFENDERS (hidden for sports that don't use defender overlay)
           ════════════════════════════════════════════════ */}
        {!sportConfig?.hideDefenderToggle && (
        <SectionHeader label="Defenders" isOpen={sections.defenders} onToggle={() => toggle('defenders')} count={defenders.length} />
        )}
        {sections.defenders && !sportConfig?.hideDefenderToggle && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* Toggle row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 12px', backgroundColor: C.surface, borderRadius: 6, marginBottom: 10,
            }}>
              <span style={{ fontSize: 12, color: C.text }}>Show Defenders</span>
              <button
                onClick={() => onToggleDefenders?.()}
                style={{
                  width: 40, height: 22, borderRadius: 11, border: 'none',
                  backgroundColor: showDefenders ? C.danger : C.border,
                  position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                }}
              >
                <span style={{
                  position: 'absolute', top: 2,
                  left: showDefenders ? 20 : 2,
                  width: 18, height: 18, borderRadius: '50%',
                  backgroundColor: '#fff', transition: 'left 0.2s',
                }} />
              </button>
            </div>

            {/* Defender list */}
            {defenders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                No defenders on the surface
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {defenders.map((def, idx) => {
                  const guardedPlayer = positions.find((p) => p.id === def.guardingId);
                  return (
                    <div
                      key={def.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', borderRadius: 4,
                        backgroundColor: 'transparent',
                        transition: 'background 0.12s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.surface; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {/* defender dot */}
                      <span style={{
                        width: 28, height: 28, borderRadius: '50%',
                        backgroundColor: C.danger, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: '#fff', flexShrink: 0,
                        border: guardedPlayer ? `2px solid ${guardedPlayer.color || C.textDim}` : 'none',
                      }}>
                        D{idx + 1}
                      </span>

                      {/* info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: C.text }}>
                          {def.label || `Defender ${idx + 1}`}
                        </div>
                        <div style={{ fontSize: 10, color: C.textDim }}>
                          {guardedPlayer ? `Guarding ${guardedPlayer.positionLabel || guardedPlayer.playerName || '?'}` : 'Unassigned'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════
           5. PLAY LIBRARY
           ════════════════════════════════════════════════ */}
        <SectionHeader label="Play Library" isOpen={sections.library} onToggle={() => toggle('library')} count={plays.length} />
        {sections.library && (
          <div style={{ padding: 12, borderBottom: `1px solid ${C.border}` }}>
            {/* New play button */}
            <button
              onClick={() => onPlayCreate?.()}
              style={{
                width: '100%', padding: '8px 0', marginBottom: 10,
                backgroundColor: C.accent, border: 'none', borderRadius: 6,
                color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = C.accentHover; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = C.accent; }}
            >
              + New Play
            </button>

            {/* Search */}
            <input
              type="text"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              placeholder="Search plays..."
              style={{ ...inputStyle, fontSize: 12, marginBottom: 10 }}
            />

            {/* Play list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'thin', scrollbarColor: `${C.border} transparent` }}>
              {filteredPlays.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 20, color: C.textDim, fontSize: 12 }}>
                  {plays.length === 0 ? 'No saved plays' : 'No matching plays'}
                </div>
              ) : (
                filteredPlays.map((play) => {
                  const isCurrent = currentPlay?.id === play.id;
                  return (
                    <div
                      key={play.id}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px',
                        backgroundColor: isCurrent ? C.accent + '18' : 'transparent',
                        borderLeft: `3px solid ${isCurrent ? C.accent : 'transparent'}`,
                        borderRadius: 4,
                        cursor: 'pointer',
                        transition: 'all 0.12s',
                      }}
                      onClick={() => onPlaySelect?.(play.id)}
                      onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = C.surface; }}
                      onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.backgroundColor = isCurrent ? C.accent + '18' : 'transparent'; }}
                    >
                      {/* info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {play.name || 'Untitled'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
                          {play.type && (
                            <span style={{
                              fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5,
                              padding: '2px 6px', borderRadius: 3,
                              backgroundColor: typeBadgeColor(play.type) + '22',
                              color: typeBadgeColor(play.type),
                            }}>
                              {play.type}
                            </span>
                          )}
                          {play.updatedAt && (
                            <span style={{ fontSize: 10, color: C.textDim }}>
                              {new Date(play.updatedAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* delete */}
                      {confirmDeleteId === play.id ? (
                        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); onPlayDelete?.(play.id); setConfirmDeleteId(null); }}
                            style={{
                              padding: '4px 10px', borderRadius: 4, border: 'none',
                              backgroundColor: C.danger, color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            Delete
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                            style={{
                              padding: '4px 8px', borderRadius: 4, border: `1px solid ${C.border}`,
                              backgroundColor: 'transparent', color: C.textMuted, fontSize: 11, cursor: 'pointer',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(play.id); }}
                          style={{
                            padding: '4px 8px', borderRadius: 4, border: 'none',
                            backgroundColor: 'transparent', color: C.textDim, fontSize: 14,
                            cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = C.danger; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = C.textDim; }}
                          title="Delete play"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── footer version ─── */}
      <div style={{
        padding: '6px 16px',
        borderTop: `1px solid ${C.border}`,
        fontSize: 10,
        color: C.textDim,
        textAlign: 'center',
        fontFamily: 'monospace',
        flexShrink: 0,
      }}>
        v0.6.0
      </div>
    </div>
  );
};

export default ControlPanel;
